import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Eraser, PenLine, Trash2, Star } from "lucide-react";
import { RatingDialog, RatingView } from "@/components/Rating";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Field, BigButton } from "@/components/Field";
import { Panel } from "@/components/Bits";
import { api, errMsg } from "@/lib/api";

export const Pad = ({ canvasRef }) => {
  const drawing = useRef(false);
  useEffect(() => {
    const c = canvasRef.current;
    const r = window.devicePixelRatio || 1;
    c.width = c.offsetWidth * r;
    c.height = c.offsetHeight * r;
    const ctx = c.getContext("2d");
    ctx.scale(r, r);
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#1B2A26";
  }, [canvasRef]);
  const pos = (e) => { const b = canvasRef.current.getBoundingClientRect(); return [e.clientX - b.left, e.clientY - b.top]; };
  const down = (e) => { drawing.current = true; canvasRef.current.setPointerCapture(e.pointerId); const ctx = canvasRef.current.getContext("2d"); ctx.beginPath(); ctx.moveTo(...pos(e)); canvasRef.current.dataset.dirty = "1"; };
  const move = (e) => { if (!drawing.current) return; const ctx = canvasRef.current.getContext("2d"); ctx.lineTo(...pos(e)); ctx.stroke(); };
  return <canvas ref={canvasRef} onPointerDown={down} onPointerMove={move} onPointerUp={() => { drawing.current = false; }}
    className="w-full h-48 rounded-2xl border-2 border-dashed border-[#C86D51]/60 bg-white touch-none" data-testid="signature-canvas" />;
};

const SignDialog = ({ open, onOpenChange, tipo, os, onSaved }) => {
  const ref = useRef();
  const [nome, setNome] = useState("");
  useEffect(() => { if (open) setNome(tipo === "tecnico" ? os.tecnico_nome || "" : os.fazenda?.responsavel || os.cliente?.responsavel || os.cliente?.nome || ""); }, [open, tipo, os]);
  const clear = () => { const c = ref.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); c.dataset.dirty = ""; };
  const save = async () => {
    if (!ref.current.dataset.dirty) return toast.error("Faça a assinatura no quadro");
    try {
      const { data } = await api.post(`/ordens/${os.os_id}/assinatura`, { tipo, nome, imagem: ref.current.toDataURL("image/png") });
      toast.success("Assinatura registrada");
      onOpenChange(false);
      onSaved(data);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-3xl" data-testid="signature-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{tipo === "tecnico" ? "Assinatura do técnico" : "Assinatura do responsável pela fazenda"}</DialogTitle></DialogHeader>
        {tipo === "cliente" && os.status === "finalizada" && (
          <p className="-mt-2 text-sm text-[#5C6B66]" data-testid="signature-finish-hint">Serviço finalizado. Entregue o aparelho ao gerente ou responsável pela fazenda para assinar a {os.numero}.</p>
        )}
        <div className="space-y-4">
          <Field label="Nome de quem assina" id="sig-nome" value={nome} onChange={(e) => setNome(e.target.value)} data-testid="signature-name-input" />
          {open && <Pad canvasRef={ref} />}
          <div className="grid grid-cols-2 gap-3">
            <BigButton type="button" variant="outline" onClick={clear} data-testid="signature-clear"><Eraser className="h-5 w-5" /> Limpar</BigButton>
            <BigButton type="button" onClick={save} disabled={nome.trim().length < 2} data-testid="signature-save">Confirmar</BigButton>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Slot = ({ label, sig, canSign, canClear, onSign, onClear, testId }) => (
  <div className="rounded-2xl border border-[#E2DDD3] p-4" data-testid={testId}>
    <p className="text-[11px] font-bold uppercase tracking-wider text-[#5C6B66]">{label}</p>
    {sig ? (
      <>
        <img src={sig.imagem} alt={`Assinatura ${label}`} className="h-24 w-full object-contain my-2 bg-[#FAF8F5] rounded-xl" />
        <p className="font-semibold text-center">{sig.nome}</p>
        {canClear && <button onClick={onClear} className="mt-2 h-11 w-full rounded-xl text-sm text-[#B05A40] font-semibold flex items-center justify-center gap-2 hover:bg-[#C86D51]/10" data-testid={`${testId}-clear`}><Trash2 className="h-4 w-4" /> Refazer</button>}
      </>
    ) : canSign ? (
      <BigButton variant="outline" className="mt-3" onClick={onSign} data-testid={`${testId}-sign`}><PenLine className="h-5 w-5" /> Assinar</BigButton>
    ) : <p className="mt-3 text-[#5C6B66] text-sm">Pendente</p>}
  </div>
);

export const Signatures = ({ os, role, onChange, ask, onAskDone }) => {
  const [tipo, setTipo] = useState(null);
  const [rate, setRate] = useState(false);
  const staffCan = os.pode_editar;
  useEffect(() => {
    if (ask && !os.assinatura_cliente) setTipo("cliente");
    if (ask) onAskDone();
  }, [ask, os.assinatura_cliente, onAskDone]);
  const clear = async (t) => {
    try { await api.delete(`/ordens/${os.os_id}/assinatura/${t}`); onChange(); } catch (e) { toast.error(errMsg(e)); }
  };
  const signed = (data) => {
    onChange();
    if (data.assinatura_cliente && !data.avaliacao && tipo === "cliente") setRate(true);
  };
  return (
    <Panel title="Assinaturas" testId="os-signatures">
      <div className="grid sm:grid-cols-2 gap-4">
        <Slot label="Técnico responsável" sig={os.assinatura_tecnico} canSign={os.pode_assinar && role !== "cliente"} canClear={staffCan} onSign={() => setTipo("tecnico")} onClear={() => clear("tecnico")} testId="sig-tecnico" />
        <Slot label="Responsável pela fazenda" sig={os.assinatura_cliente} canSign={os.pode_assinar} canClear={staffCan} onSign={() => setTipo("cliente")} onClear={() => clear("cliente")} testId="sig-cliente" />
      </div>
      {os.avaliacao ? <RatingView av={os.avaliacao} /> : os.assinatura_cliente && os.pode_assinar && (
        <BigButton variant="outline" className="mt-4" onClick={() => setRate(true)} data-testid="rating-open"><Star className="h-5 w-5 text-[#E0A100]" /> Avaliar o serviço</BigButton>
      )}
      <SignDialog open={!!tipo} onOpenChange={(o) => !o && setTipo(null)} tipo={tipo} os={os} onSaved={signed} />
      <RatingDialog open={rate} onOpenChange={setRate} os={os} onSaved={onChange} />
    </Panel>
  );
};
